import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PersistGate } from 'redux-persist/integration/react';
import AppBase from '@atlassian/jira-app-base';
import { initBrowserMetrics3 } from '@atlassian/jira-browser-metrics';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { SpotlightManager, TextField } from '@atlassian/jira-portfolio-3-common';
import { context } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics';
import { initialLoad } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command';
import { setInitialFilterValues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/filters';
import store, {
	persistor,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/store';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import FieldUserAvatarCell from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/component-slot-content/field-user-avatar-cell';
import PortfolioErrorBoundary from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-boundary';
import ErrorNotification from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-notification';
import GoalsWrapper from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/goal-provider';
import NowLine from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/now';
import { provideComponents } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import {
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { CellSelectWithRollup } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/select';
import { getQueryParameters } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import AdvancedFieldsMenu from '../component-slot-content/advanced-fields-menu';
import FieldDatePicker from '../component-slot-content/field-date-picker';
import FieldMultiSelectCell from '../component-slot-content/field-multi-select-cell';
import FieldPrioritySelect from '../component-slot-content/field-priority-select';
import FieldSelect from '../component-slot-content/field-select';
import UserPicker from '../component-slot-content/field-user-picker';
import IssueSearchWarningClearFilter from '../component-slot-content/issue-search-warning-clear-filter';
import mapStateToProps from './query';
import type { DispatchProps, OwnProps, StateProps } from './types';
import View from './view';

const mapDispatchToProps = {
	initialLoad,
	setInitialFilterValues,
} as const;

const Connected = connect<StateProps, DispatchProps, OwnProps, State>(
	mapStateToProps,
	mapDispatchToProps,
)(View);

type Props = {
	locale: Locale;
};

const App = ({ locale }: Props) => {
	const { r: reportId } = getQueryParameters();

	useEffect(() => {
		/**
		 * Initializing the BM3 performance instrumentation for legacy page. It starts the
		 * page load and also adds some performance related metadata. The metric is stopped in
		 * src/packages/portfolio-3/portfolio/src/common/analytics/performance.js#L122
		 */
		initBrowserMetrics3?.();

		if (ff('com.atlassian.rm.jpo.jpo3cloud.centralise-react-virtualized-overflow-styles')) {
			/**
			 * We are adding this special class to 'jira-frontend' div to scope the react virtualized
			 * classes defined in src/app-simple-plans/styles.css.
			 * When we are in Jira SPA, we can not pollute the global classes and hence we are
			 * scoping them to ARJ application.
			 * We remove the special .arj-report-view class once our app is unmounted. Look into the returned function below.
			 */
			const JfeRootElement = document.getElementById('jira-frontend');
			if (JfeRootElement) {
				JfeRootElement.classList.add('arj-report-view');
			}

			return function removeArjClass() {
				// eslint-disable-next-line @typescript-eslint/no-shadow
				const JfeRootElement = document.getElementById('jira-frontend');
				if (JfeRootElement) {
					JfeRootElement.classList.remove('arj-report-view');
				}
			};
		}
	}, []);

	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	useEffect(() => {}, []);

	return (
		<AppBase store={store} hasErrorBoundary={false}>
			<GoalsWrapper>
				<ContextualAnalyticsData
					sourceType={SCREEN}
					sourceName="portfolio3ConfluenceReport"
					attributes={{ reportId, context: context.PLAN }}
				>
					{/**
                     PortfolioErrorBoundary is portfolio specific error boundary and is used to
                     show fallback UI when React tree is broken and also show error flags for
                     other errors.
                     */}
					<PortfolioErrorBoundary>
						{/**
                         ReportErrors is an error boundary provided by JFP. It reports errors to
                         SignalFX and Sentry. It report error messages and error stacktraces only to
                         Sentry, but not to SignalFX. SignalFX only gets error count.
                         This component also rethrows the error, so that we can show fallback UI.
                         */}
						<ReportErrors
							id={ERROR_REPORTING_PACKAGE.APP}
							packageName={PACKAGE_NAME}
							teamName={ERROR_REPORTING_TEAM}
							sendToPrivacyUnsafeSplunk
						>
							{document.body && createPortal(<ErrorNotification />, document.body)}
							<PersistGate loading={null} persistor={persistor}>
								<SpotlightManager>
									<Connected locale={locale} />
								</SpotlightManager>
							</PersistGate>
						</ReportErrors>
					</PortfolioErrorBoundary>
				</ContextualAnalyticsData>
			</GoalsWrapper>
		</AppBase>
	);
};

export default provideComponents<Props>({
	FieldSelect,
	FieldPrioritySelect,
	FieldMultiSelectCell,
	FieldDatePicker,
	IssueSearchWarningClearFilter,
	AdvancedFieldsMenu,
	FieldUserAvatarCell,
	NowLine,
	UserPicker,
	CellSelectWithRollup,
	CellTextField: TextField,
})(App);
